import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  ButtonToggle,
  Flex,
  Skeleton,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import type {
  ReservationMode,
  ShopperReservationMode,
} from '@iheartjane/types';
import { useEffect, useRef } from 'react';

import { resolveSelectedMode } from '@jane/shared-ecomm/pages';
import { useEcommApp } from '@jane/shared/providers';

import { useCustomerDispatch } from '../../../../customer/dispatch';
import {
  selectBagPickupMode,
  selectCart,
  setBagPickupMode,
  updateReservationMode,
} from '../../../../customer/redux/cart';
import { useCustomerSelector } from '../../../../customer/selectors';
import styles from './cartDrawerToggle.module.css';

interface CartDrawerToggleProps {
  /**
   * Internal flag to configure headless styles to be initially tested in Bloom
   * @private
   */
  bloomUserExperience?: boolean;
  curbsideLabel: string;
  deliveryAddress?: string;
  deliveryLabel: string;
  hasCurbsideSlots: boolean;
  hasDeliverySlots: boolean;
  hasPickupSlots: boolean;
  isAcceptingDeliveryOrders: boolean;
  isLoading: boolean;
  modesToRender: ShopperReservationMode[];
  pickupLabel: string;
  refetchCurbside: () => void;
  refetchDelivery: () => void;
  refetchPickup: () => void;
}

export const CartDrawerToggle = ({
  bloomUserExperience,
  curbsideLabel,
  deliveryAddress,
  deliveryLabel,
  hasCurbsideSlots,
  hasDeliverySlots,
  hasPickupSlots,
  isAcceptingDeliveryOrders,
  isLoading,
  modesToRender,
  pickupLabel,
  refetchCurbside,
  refetchDelivery,
  refetchPickup,
}: CartDrawerToggleProps) => {
  const hasToggledRef = useRef(false);
  const { appMode } = useEcommApp();
  const isHeadless = appMode === 'headless' || bloomUserExperience;
  const dispatch = useCustomerDispatch();
  const {
    cart: { reservation_mode: currentReservationMode },
    userGroupSpecialId,
  } = useCustomerSelector(selectCart);
  const bagPickupMode = useCustomerSelector(selectBagPickupMode);
  const curbsideFlagOn = useFlag(FLAGS.c1CurbsideFulfillment) ?? false;

  const labelFor = (mode: ShopperReservationMode) =>
    mode === 'delivery'
      ? deliveryLabel
      : mode === 'pickup'
        ? pickupLabel
        : curbsideLabel;

  const handleReservationModeToggle = (reservationMode: ReservationMode) => {
    dispatch(
      updateReservationMode({
        reservationMode,
        userGroupSpecialId,
      })
    );
  };

  const handleModeChange = (mode: ShopperReservationMode) => {
    hasToggledRef.current = true;
    if (mode === 'delivery') {
      handleReservationModeToggle('delivery');
      // Reset the pickup sub-mode so a prior 'curbside' choice doesn't re-seed
      // the checkout form if the shopper switches back.
      if (curbsideFlagOn) dispatch(setBagPickupMode('pickup'));
      if (deliveryAddress) {
        refetchDelivery();
      } else {
        refetchPickup();
      }
    } else {
      // Curbside and pickup both submit as 'pickup'; bagPickupMode distinguishes.
      handleReservationModeToggle('pickup');
      if (curbsideFlagOn) dispatch(setBagPickupMode(mode));
      if (mode === 'curbside') {
        refetchCurbside();
      } else {
        refetchPickup();
      }
    }
  };

  // Raw (drives the mount realign) vs clamped to modesToRender (drives render).
  const desiredMode = resolveSelectedMode(
    currentReservationMode,
    bagPickupMode
  );
  const selectedMode = resolveSelectedMode(
    currentReservationMode,
    bagPickupMode,
    modesToRender
  );

  useEffect(() => {
    // Mount-only realign when the persisted selection isn't available. Empty
    // deps: initial sync only; user toggles go through handleModeChange.
    if (modesToRender.includes(desiredMode)) return;

    const target = selectedMode;
    const wireMode: ReservationMode =
      target === 'delivery' ? 'delivery' : 'pickup';
    if (wireMode !== currentReservationMode) {
      handleReservationModeToggle(wireMode);
    }
    if (curbsideFlagOn) {
      dispatch(setBagPickupMode(target === 'curbside' ? 'curbside' : 'pickup'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const numberOfModes = modesToRender?.length || 2;

  return (
    <Flex width="100%" justifyContent="center" mb={isHeadless ? 16 : 0} mt={16}>
      {isLoading ? (
        <Flex width="100%" gap={16}>
          {Array.from({ length: numberOfModes }).map((_, index) => (
            <Skeleton.Bone
              height="48px"
              width={numberOfModes > 1 ? '50%' : '100%'}
              borderRadius="lg"
              key={index}
            />
          ))}
        </Flex>
      ) : numberOfModes > 1 ? (
        <ButtonToggle
          variant="tertiary"
          value={selectedMode}
          onChange={(mode) => handleModeChange(mode as ShopperReservationMode)}
        >
          {modesToRender.map((mode) => {
            const isAcceptingDelivery =
              isAcceptingDeliveryOrders || hasDeliverySlots;

            const isClosed =
              (mode === 'delivery' && !isAcceptingDelivery) ||
              (mode === 'pickup' && !hasPickupSlots) ||
              (mode === 'curbside' && !hasCurbsideSlots);

            return (
              <ButtonToggle.Button
                autoFocus={hasToggledRef.current && mode === selectedMode}
                className={
                  bloomUserExperience
                    ? styles['premiumToggleButton']
                    : undefined
                }
                key={mode}
                value={mode}
                disabled={isClosed}
              >
                <Flex justifyContent="center">
                  <Typography
                    color="inherit"
                    as="span"
                    variant="body-bold"
                    maxLines={2}
                    overflowWrap="break-word"
                  >
                    {labelFor(mode)}
                  </Typography>
                  {isClosed && (
                    <Tag label="Closed" background="grays-black" ml={4} />
                  )}
                </Flex>
              </ButtonToggle.Button>
            );
          })}
        </ButtonToggle>
      ) : (
        <Typography variant="body-bold" textAlign="center">
          {labelFor(selectedMode)}
        </Typography>
      )}
    </Flex>
  );
};
